(() => {
  const ROOT_SELECTOR = '[id^="WICollection-"]';
  const CARD_SELECTOR = '[data-wi-secondary-image]';
  const states = new WeakMap();

  function createSecondaryImage(card, highPriority = false) {
    if (!card || card.dataset.wiSecondaryReady) return;

    const media = card.querySelector('.wi-product-card__media');
    const src = card.dataset.secondarySrc;

    if (!media || !src) return;

    card.dataset.wiSecondaryReady = 'loading';

    const image = document.createElement('img');

    image.className =
      'wi-product-card__image wi-product-card__image--secondary';

    image.alt = card.dataset.secondaryAlt || '';
    image.width = Number(card.dataset.secondaryWidth) || 720;
    image.height = Number(card.dataset.secondaryHeight) || 900;
    image.decoding = 'async';
    image.loading = highPriority ? 'eager' : 'lazy';
    image.fetchPriority = highPriority ? 'auto' : 'low';
    image.setAttribute('aria-hidden', 'true');

    if (card.dataset.secondarySrcset) {
      image.srcset = card.dataset.secondarySrcset;
      image.sizes = '(min-width: 990px) 25vw, 50vw';
    }

    image.addEventListener(
      'load',
      () => {
        card.dataset.wiSecondaryReady = 'true';
      },
      { once: true }
    );

    image.addEventListener(
      'error',
      () => {
        image.remove();
        delete card.dataset.wiSecondaryReady;
      },
      { once: true }
    );

    const badges = media.querySelector(
      '.wi-product-card__image-badges'
    );

    const exclusiveBadge = media.querySelector(
      '.wi-product-card__exclusive-badge'
    );

    media.insertBefore(image, badges || exclusiveBadge || null);

    image.src = src;
  }

  function observeCards(root, state) {
    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (card.dataset.wiSecondaryReady) return;

      if (state.intersectionObserver) {
        state.intersectionObserver.observe(card);
      }
    });
  }

  function initialiseCollection(root) {
    if (!root || states.has(root)) return;

    const state = {
      intersectionObserver: null,
      gridObserver: null
    };

    if ('IntersectionObserver' in window) {
      state.intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            createSecondaryImage(entry.target, false);
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '300px 0px',
          threshold: 0.01
        }
      );
    }

    root.addEventListener(
      'pointerover',
      (event) => {
        const card = event.target.closest(CARD_SELECTOR);

        if (!card || !root.contains(card)) return;

        createSecondaryImage(card, true);
      },
      { passive: true }
    );

    root.addEventListener('focusin', (event) => {
      const card = event.target.closest(CARD_SELECTOR);

      if (!card || !root.contains(card)) return;

      createSecondaryImage(card, true);
    });

    const gridContainer = root.querySelector(
      '#ProductGridContainer'
    );

    if (gridContainer && 'MutationObserver' in window) {
      let refreshFrame = null;

      state.gridObserver = new MutationObserver(() => {
        if (refreshFrame) {
          window.cancelAnimationFrame(refreshFrame);
        }

        refreshFrame = window.requestAnimationFrame(() => {
          observeCards(root, state);
          refreshFrame = null;
        });
      });

      state.gridObserver.observe(gridContainer, {
        childList: true
      });
    }

    states.set(root, state);
    observeCards(root, state);
  }

  function initialiseAllCollections(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initialiseCollection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseAllCollections(),
      { once: true }
    );
  } else {
    initialiseAllCollections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAllCollections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const roots = event.target.querySelectorAll(ROOT_SELECTOR);

    roots.forEach((root) => {
      const state = states.get(root);

      if (!state) return;

      state.intersectionObserver?.disconnect();
      state.gridObserver?.disconnect();
      states.delete(root);
    });
  });
})();